import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { Observable, of, Subject } from 'rxjs';
import { DocumentsService, SignedDocumentsService } from 'src/app/api/services';
import { Client, ClientDocument, ClientDocumentType } from 'src/app/api/models';
import { SignDocumentRequestDialogComponent } from 'src/app/components/sign-document-request-dialog/sign-document-request-dialog.component';
import { CountriesMap, CountriesMapCache } from 'src/app/model/model-map-cache';
import { ClientPeerService } from '../../service/client-peer.service';
import { shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';
import { LightEntrepreneurClientEditDialogComponent } from 'src/app/components/light-entrepreneur-client-edit-dialog/light-entrepreneur-client-edit-dialog.component';


// AI: This page is for LightEntrepreneurClient.
@Component({
  selector: 'app-light-entrepreneur-client-page',
  templateUrl: './light-entrepreneur-client-page.component.html',
  styleUrls: ['./light-entrepreneur-client-page.component.scss'],
  providers: [ClientPeerService]
})
export class LightEntrepreneurClientPageComponent implements OnInit, OnDestroy {
  private readonly ngDestroy$ = new Subject();

  SigningStatus = Object.freeze({ UNSIGNED: 1, PENDING: 2, SIGNED: 3, FAILED: 4 })

  countries$: Observable<CountriesMap>;
  client$: Observable<Client>;

  documents$: Observable<ClientDocument[]>;
  documentTypes$: Observable<ClientDocumentType[]>;

  constructor(private clientPeerService: ClientPeerService,
    private documentsService: DocumentsService,
    private signedDocumentsService: SignedDocumentsService,
    private countriesMapCache: CountriesMapCache,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router) {
  }

  dateToString(date: Date): string {
    return date.toISOString();
  }

  openEditDialog(): void {
    this.client$.pipe(take(1)).subscribe(client => {
      const dialogRef = this.dialog.open(LightEntrepreneurClientEditDialogComponent, {
        width: '800px',
        data: { client: client }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.clientPeerService.refresh();
        }
      });
    });
  }

  downloadClientDocument(doc: ClientDocument): void {
    if (doc.signed_document && doc.signed_document.status === 'signed') {
      this.downloadSignedClientDocument(doc);
    } else {
      this.downloadOriginalClientDocument(doc);
    }
  }

  downloadOriginalClientDocument(doc: ClientDocument): void {
    this.documentsService.getClientDocumentUrl(doc).subscribe(fileUrl => {
      console.log(fileUrl);
      window.open(fileUrl.url, '_blank');
    });
  }

  downloadSignedClientDocument(doc: ClientDocument): void {
    this.signedDocumentsService.getSignedDocumentUrl(doc.signed_document).subscribe(fileUrl => {
      console.log(fileUrl);
      window.open(fileUrl.url, '_blank');
    });
  }

  clientDocumentFileAdded(client: Client, file: File): void {
    this.documentsService.createClientDocument({
      client_id: client.id,
      body: {
        file: file,
      },
    }).pipe(take(1))
      .subscribe(() => {
        this.clientPeerService.refresh();
      });
  }

  confirmDeleteClientDocument(doc: ClientDocument): void {
    const dialogData = new ConfirmationDialogModel('Delete Confirmation', `Are you sure you want to delete the ${doc.type_description} document ${doc.filename}?`);
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.documentsService.deleteClientDocument(doc)
          .pipe(take(1))
          .subscribe(() => {
            this.clientPeerService.refresh();
          });
      }
    });
  }

  documentSigningStatus(doc: ClientDocument): number {
    if (!doc.signed_document) {
      return this.SigningStatus.UNSIGNED;
    }
    if (doc.signed_document?.status === 'signed') {
      return this.SigningStatus.SIGNED;
    }
    if (doc.signed_document?.status === 'cancelled' || doc.signed_document?.status === 'deleted') {
      return this.SigningStatus.FAILED;
    }
    return this.SigningStatus.PENDING
  }

  canSendSignal(doc: ClientDocument): boolean {
    return this.documentSigningStatus(doc) === this.SigningStatus.PENDING
  }

  remindSigners(doc: ClientDocument): void {
    const dialogData = new ConfirmationDialogModel('Remind Confirmation', `Are you sure you want to remind signers to sign the '${doc.name}' (${doc.filename})?`);
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.signedDocumentsService.remindInvitees(doc.signed_document)
          .pipe(take(1))
          .subscribe(() => {
            this.clientPeerService.refresh();
          });
      }
    });
  }

  cancelDocumentSigning(doc: ClientDocument): void {
    const dialogData = new ConfirmationDialogModel('Cancel Signing Confirmation', `Are you sure you want to cancel the signing process for '${doc.name}' (${doc.filename})?`);
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.signedDocumentsService.cancelSigning(doc.signed_document)
          .pipe(take(1))
          .subscribe(() => {
            this.clientPeerService.refresh();
          });
      }
    });
  }

  changeClientDocumentType(doc: ClientDocument, docType: ClientDocumentType): void {
    doc.type_id = docType.id;

    this.documentsService.updateClientDocument({
      client_id: doc.client_id,
      id: doc.id,
      body: doc,
    }).pipe(take(1))
      .subscribe(() => {
        this.clientPeerService.refresh();
      });
  }

  openSignDocumentRequestDialog(doc: ClientDocument): void {
    const dialogRef = this.dialog.open(SignDocumentRequestDialogComponent, {
      data: { document: doc },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.clientPeerService.refresh();
      }
    });
  }

  ngOnInit() {
    this.countries$ = this.countriesMapCache.map$;
    this.documentTypes$ = this.documentsService.getClientDocumentTypes();

    this.client$ = this.clientPeerService.get().pipe(shareReplay(1));

    this.documents$ = this.client$
      .pipe(
        switchMap(client => {
          if (client && client.id > 0) {
            return this.documentsService.getClientDocuments({ client_id: client.id })
          } else {
            return of([]);
          }
        }),
      );

    this.route.paramMap.pipe(takeUntil(this.ngDestroy$))
      .subscribe((params: ParamMap) => {
        if (params.has('clientId')) {
          this.clientPeerService.initialize(+params.get('clientId'))
        } else {
          this.clientPeerService.create();
        }
      });
  }

  ngOnDestroy() {
    this.ngDestroy$.next(undefined);
    this.ngDestroy$.complete();
  }
}
